import { Canvas } from "fabric/*";
import React, { useEffect, useState } from "react";
import { CloseButton, Spinner } from "react-bootstrap";
import { ModalType, useModal } from "../../Context/ModalContext";
import { makeXhrRequest } from "../../utils/makeXhrRequest";
import { decodeHtmlEntities, TypingEffectWithHighlights } from "./ModalUtils";


interface TextPropertiesSelectorModalProps {
  canvas: Canvas;
  option?: string;
}

function AITextGeneratorModal({ canvas }: TextPropertiesSelectorModalProps) {
  const { show, toggleShow, type } = useModal();
  const [prompt, setPrompt] = useState("");
  const [data, setData] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [placeholder, setPlaceholder] = useState("");
  const [loopIndex, setLoopIndex] = useState(0);
  const [isWriting, setIsWriting] = useState(true);

  const typingSpeed = 20;
  const displayDuration = 1000;

  const texts = [
    "Souhaiter l'anniversaire de mon frère avec espièglerie",
    "Souhaiter avec humour l'anniversaire de ma mamie qui adore les fleurs et le chocolat suisse",
    "Dire Bonjour à une copine que je n'ai pas vue depuis 3 ans",
    "Présenter mes condoléances de manière solennelle à un collègue de travail pour le décès de sa mère",
    "Déclarer avec ferveur mon amour à la femme qui partage ma vie depuis 25 ans et avec qui j'ai 3 enfants et un labrador",
  ];

  /**
   * Initiates an asynchronous request to fetch AI-generated text based on the given prompt.
   *
   * @param {string | number | boolean} aiPrompt - The input parameter to be sent to the AI for generating text.
   * Accepts either a string, number, or boolean as the prompt.
   *
   * Handles the following:
   * - Sets the loading state to true while the request is being processed.
   * - Sends a POST request to a predefined URL with the AI prompt encoded as the request body.
   * - Updates the loading state and sets the appropriate response data upon successful completion of the request.
   * - Catches and handles errors by setting the error state.
   * - Ensures the loading state resets, even if the request fails.
   */
  const fetchGeneratedText = (aiPrompt: string | number | boolean) => {
    setLoading(true);
    setError(null);

    const url = `${process.env.PREFIX_URL}/v2017/ajax/ajax.php?traitement=ia-mf&type=carte&tone=neutre`;
    const method = "POST";
    const headers = {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Requested-With": "XMLHttpRequest",
      Accept: "application/json",
    };
    const body = `rq=${encodeURIComponent(aiPrompt)}`;

    makeXhrRequest(
      url,
      method,
      headers,
      body,
      (data) => {
        setLoading(false);
        setData(data.result);
      },
      (errorMessage) => {
        setLoading(false);
        setError(errorMessage);
      },
      () => setLoading(false)
    );

  };

  /**
   * Updates the text content of a textbox object on a canvas and triggers a re-render of the canvas.
   *
   * If a textbox object exists on the canvas, its text property is updated
   * with the provided value after decoding any HTML entities. The canvas is then re-rendered
   * to reflect these changes. Additionally, the visibility of a specific modal is toggled off.
   *
   * @param {string} value - The new text value to be set for the textbox, with HTML entities decoded.
   */
  const handleTxtEdit = (value: string) => {
    const textObject = canvas._objects.find((obj) => obj.type === "textbox");
    if (textObject) {
      textObject.set({ text: decodeHtmlEntities(value) });
      canvas.renderAll();
      toggleShow(false, null, ModalType.aItextGenerator);
    }
  };

  /**
   * Event handler function for handling AI text click events.
   * This function ensures that the click event is only handled
   * when the target of the event is the same as the current target.
   *
   * @param {React.MouseEvent} e - The click event triggered by the user interaction.
   */
  const handleAiTextClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      toggleShow(false, null, type);
    }
  };

  useEffect(() => {
    let timer;

    if (isWriting) {
      const currentText = texts[loopIndex % texts.length];
      if (placeholder.length < currentText.length) {
        timer = setTimeout(() => {
          setPlaceholder(currentText.slice(0, placeholder.length + 1));
        }, typingSpeed);
      } else {
        setTimeout(() => setIsWriting(false), displayDuration);
      }
    } else {
      setPlaceholder("");
      setIsWriting(true);
      setLoopIndex((prev) => (prev + 1) % texts.length);
    }

    return () => clearTimeout(timer);
  }, [placeholder, isWriting, loopIndex, texts]);

  return (
    <div 
      className={`custom-modal-background ${show && type === ModalType.aItextGenerator ? "" : "hidden"}`}
      onClick={handleAiTextClick}
    >
      <div
        className={`custom-modal ${show && type === ModalType.aItextGenerator ? "" : "hidden"}`}
        aria-labelledby="contained-modal-title-vcenter"
      >
      <div className="custom-modal-content bg-white">
        <div className="custom-modal-header">
          <h4 className="custom-modal-title text-muted">
            De quoi doit parler votre carte ?
          </h4>
          <CloseButton
            onClick={() => toggleShow(false, null, type)}
            aria-label="Close"
          />
        </div>
        <div className="custom-modal-body">
          <div className="py-3">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={placeholder}
              className="custom-textarea"
            />
            {data === "" && !loading && (
              <em
                style={{
                  color: "#585454",
                  lineHeight: "1.4",
                  fontStyle: "italic",
                  fontFamily: "'Work Sans',sans-serif",
                }}
              >
                Détaillez ci-dessus les objectifs de votre carte en indiquant un
                maximum d'informations (goûts, prénom, age, ton du texte, etc.),
                l'écriture intelligente fera le reste.
              </em>
            )}
            {data.length > 0 && (
              <>
                <p className="text-secondary" style={{fontWeight: "bold"}}>
                  Voici ma proposition de texte :
                </p>
                <TypingEffectWithHighlights text={data} typingSpeed={20} />
              </>
            )}
          </div>
          <div className="w-100 d-flex flex-column justify-content-center gap-3 py-3 items-center">
            {loading && (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
            {error && <p className="text-danger">Erreur : {error}</p>}
          </div>
          <div className="d-flex gap-3 justify-content-end">
            {data === "" && !loading && <div
              className="btn custom-button"
              onClick={() => {
                fetchGeneratedText(prompt);
              }}
            >
              <span>Lancer l'écriture intelligente</span>
            </div>}
            {data.length > 0 && (
              <>
                <div
                  className="btn custom-button-outline"
                  onClick={() => {
                    setData("");
                    fetchGeneratedText(prompt);
                  }}
                >
                  <span>Relancer l'écriture intelligente</span>
                </div>
                <div
                  className="btn custom-button"
                  onClick={() => {
                    handleTxtEdit(data);
                  }}
                >
                  <span>Utiliser ce texte 👉</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default AITextGeneratorModal;
